/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { CircularProgress, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import { EntityThumbnail } from '../../components/EntityThumbnail/EntityThumbnail';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { NoItemsText } from '../../components/NoItemsText/NoItemsText';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { OrganizationUserApiService } from '../../services/OrganizationUserApiService';
import { UserApiService } from '../../services/UserApiService';
import { RootState } from '../../store';
import { ApiClientError } from '../../types/api/ApiClientError';
import { EntityId } from '../../types/api/EntityId';
import { Organization } from '../../types/organization/Organization';
import { OrganizationInvitation } from '../../types/user/OrganizationInvitation';
import { isDataLoading, isDataNotAsked } from '../../utils/dataState';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { reactToResolveOrganizationInvitation } from '../../utils/reactions';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useObjectState } from '../../utils/useObjectState';
import { useOrganizationRolesI18n } from '../../utils/useOrganizationRolesI18n';
import { useOrganizationsPageStyles } from './OrganizationsPage.styles';

const mapState = (state: RootState) => ({
  auth: state.auth,
  organizations: state.organizations,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

export type InvitationsPageProps = PropsFromRedux;

type State = {
  isOpenErrorModal: boolean;
  isOpenLeaveModal: boolean;
  organization?: Organization;
  errorMessage?: string;
};

const OrganizationsPageComponent = memo<InvitationsPageProps>(props => {
  const { t } = useTranslation(Namespaces.UserProfile);
  useDocumentTitle(t(`${Namespaces.Common}:page_titles.organizations`));

  const classes = useOrganizationsPageStyles();
  const organizationRoles = useOrganizationRolesI18n();

  const {
    auth: { user, isFetching: isFetchingAuth, isRefreshing: isRefreshingAuth },
    organizations: { options: myOrgs, dataState },
  } = props;
  const navigate = useNavigate();

  const [state, setState] = useObjectState<State>({
    isOpenErrorModal: false,
    isOpenLeaveModal: false,
  });

  const { isOpenErrorModal, isOpenLeaveModal, errorMessage, organization } = state;
  const invitations = useMemo(() => user?.invitations ?? [], [user?.invitations]);

  const handleToggleErrorModal = () =>
    setState({
      ...state,
      isOpenErrorModal: !isOpenErrorModal,
    });

  const handleHideLeaveModal = useCallback(
    () =>
      setState({
        ...state,
        organization: undefined,
        isOpenLeaveModal: false,
      }),
    [setState, state],
  );

  const handleShowLeaveModal = useCallback(
    (organization: Organization) => () =>
      setState({
        ...state,
        organization,
        isOpenLeaveModal: true,
      }),
    [setState, state],
  );

  const handleMutationError = (e: ApiClientError) => {
    console.error(e);
    const errorMessage = e.response?.data.message;

    if (errorMessage) {
      setState({
        isOpenErrorModal: true,
        errorMessage,
      });
    }
  };

  const [resolveOrganizationInvitation, { isLoading: isLoadingOrganizationInvitation }] = useMutation(
    UserApiService.resolveOrganizationInvitation,
    {
      onError: (error: ApiClientError) => handleMutationError(error),
    },
  );

  const [leaveOrganization, { isLoading: isLoadingLeaveOrganization }] = useMutation(
    OrganizationUserApiService.deleteUserFromOrganization,
    {
      onError: (error: ApiClientError) => handleMutationError(error),
    },
  );

  const isFetching = useMemo(
    () => isLoadingOrganizationInvitation || isLoadingLeaveOrganization,
    [isLoadingLeaveOrganization, isLoadingOrganizationInvitation],
  );

  const handleResolveInvite = useCallback(
    (invite: OrganizationInvitation, accept: boolean) => async () => {
      await resolveOrganizationInvitation({
        invitationId: invite.id,
        accept,
      });
      await reactToResolveOrganizationInvitation({
        organizationId: invite.organization.id,
      });
    },
    [resolveOrganizationInvitation],
  );

  const handlePushToOrganization = useCallback(
    (id: EntityId) => () =>
      navigate(
        prepareRoutePath(BasicRoutes.ORGANIZATION_PAGE, {
          ':organization_id': id,
        }),
      ),
    [navigate],
  );

  const handleLeaveOrganization = useCallback(async () => {
    if (!organization) {
      return;
    }

    handleHideLeaveModal();

    await leaveOrganization({
      organizationId: organization.id,
      userId: user!.id,
    });

    await reactToResolveOrganizationInvitation({
      organizationId: organization.id,
    });
  }, [handleHideLeaveModal, leaveOrganization, organization, user]);

  const isLoading = useMemo(() => isFetchingAuth || isDataNotAsked(dataState), [isFetchingAuth, dataState]);

  const isSpinning = useMemo(
    () => isFetching || isRefreshingAuth || isDataLoading(dataState),
    [isRefreshingAuth, isFetching, dataState],
  );

  const organizationsTable = useMemo(() => {
    const sortedOrganizations = myOrgs.sort((a, b) => a.name.localeCompare(b.name));

    return (
      <Box className={classes.wrapper}>
        <Typography variant="subtitle2">{t('invitations.label.your_organizations')}</Typography>
        <Typography variant="subtitle2">{t('invitations.label.role')}</Typography>
        <Divider className={classes.divider} />
        {sortedOrganizations.map((organization, i) => (
          <>
            <Box className={classes.cell}>
              <Box
                display="flex"
                flexDirection="row"
                gap={2}
                alignItems="center"
                justifyContent="left"
                onClick={handlePushToOrganization(organization.id)}
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
              >
                <EntityThumbnail
                  size={32}
                  type="organization"
                  organizationId={organization.id}
                  allowUploadImage={false}
                />
                &nbsp;
                {organization.name}
              </Box>
            </Box>
            <Box className={classes.cell}>{organizationRoles.find(role => role.key === organization.role)?.title}</Box>
            <Box className={classes.cell} justifySelf="end">
              <Tooltip title={t(`${Namespaces.Common}:label.leave_org`)}>
                <IconButton
                  onClick={handleShowLeaveModal(organization)}
                  disabled={isFetching}
                  size="small"
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  data-cy="leave-organization-button"
                >
                  <ExitToAppIcon />
                </IconButton>
              </Tooltip>
            </Box>
            {i + 1 !== sortedOrganizations.length ? <Divider className={classes.divider} /> : null}
          </>
        ))}
      </Box>
    );
  }, [
    classes.cell,
    classes.divider,
    classes.wrapper,
    handlePushToOrganization,
    handleShowLeaveModal,
    isFetching,
    myOrgs,
    organizationRoles,
    t,
  ]);

  const invitationsTable = useMemo(
    () => (
      <Box marginBottom={4}>
        <Typography variant="subtitle1" marginBottom={2}>
          {t('invitations.title')}
        </Typography>
        <Box className={classes.wrapper}>
          <Typography variant="subtitle2">{t('invitations.label.you_are_invited_to')}</Typography>
          <Typography variant="subtitle2">{t('invitations.label.invited_by')}</Typography>
          <Divider className={classes.divider} />
          {invitations.map((invitation, i) => (
            <>
              <Box className={classes.cell}>
                <Box display="flex" flexDirection="row" gap={2} alignItems="center" justifyContent="left">
                  <EntityThumbnail
                    size={32}
                    type="organization"
                    organizationId={invitation.organization.id}
                    allowUploadImage={false}
                  />
                  &nbsp;
                  {invitation.organization.name}
                </Box>
              </Box>
              <Box className={classes.cell}>{invitation.organization.companyName}</Box>
              <Box className={classes.cell} justifySelf="end">
                <Box display="flex" flexDirection="row" gap={2} alignItems="center" justifyContent="center">
                  <Tooltip title={t(`${Namespaces.Common}:label.accept`)}>
                    <IconButton
                      onClick={handleResolveInvite(invitation, true)}
                      disabled={isFetching}
                      size="small"
                      color="primary"
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                    >
                      <CheckIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t(`${Namespaces.Common}:label.decline`)}>
                    <IconButton
                      onClick={handleResolveInvite(invitation, false)}
                      disabled={isFetching}
                      size="small"
                      color="error"
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {i + 1 !== invitations.length ? <Divider className={classes.divider} /> : null}
            </>
          ))}
        </Box>
      </Box>
    ),
    [classes.cell, classes.divider, classes.wrapper, handleResolveInvite, invitations, isFetching, t],
  );

  return (
    <div className={classes.root}>
      {isLoading ? (
        <CircularProgress color="primary" size={60} />
      ) : myOrgs && invitations && user ? (
        <>
          <HUSpin spinning={isSpinning} delay={50}>
            {invitations.length ? invitationsTable : null}
            {myOrgs.length ? (
              organizationsTable
            ) : (
              <NoItemsText
                className={classes.noItemsLabel}
                title={t(`${Namespaces.Common}:no_item_text.no_organizations`)}
              />
            )}
          </HUSpin>

          <SimpleNotifyModal
            open={isOpenErrorModal}
            message={t(
              [
                `${Namespaces.Common}:leave_organization_errors.${errorMessage}`,
                `${Namespaces.Common}:errors.unexpected_error`,
              ],
              { organization_name: organization?.name },
            )}
            onOk={handleToggleErrorModal}
          />
          <SimpleConfirmModal
            open={isOpenLeaveModal}
            confirmButtonText={t('organizations.modal.leave')}
            cancelButtonText={t('organizations.modal.cancel')}
            message={t('organizations.modal.message', {
              organizationName: organization?.name,
            })}
            onCancel={handleHideLeaveModal}
            onConfirm={handleLeaveOrganization}
          />
        </>
      ) : (
        <NoItemsText className={classes.noItemsLabel} title={t(`${Namespaces.Common}:no_item_text.no_organizations`)} />
      )}
    </div>
  );
});

export const OrganizationsPage = connector(OrganizationsPageComponent);
